import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Heart, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DonateSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const pollPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("pending");
        return;
      }

      try {
        const response = await axios.get(`${API}/donations/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentInfo(data);
        } else if (data.status === "expired") {
          setStatus("expired");
        } else {
          setTimeout(() => setAttempts(prev => prev + 1), pollInterval);
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        setTimeout(() => setAttempts(prev => prev + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts]);

  if (status === "checking" || (status !== "success" && status !== "error" && status !== "expired" && attempts < 5)) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-12 text-center">
            <Loader2 className="w-16 h-16 text-teal-600 mx-auto mb-6 animate-spin" />
            <h2 className="font-serif text-2xl font-semibold text-slate-900 mb-2">
              Verifying Your Donation
            </h2>
            <p className="text-slate-600">
              Please wait while we confirm your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error" || status === "expired") {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="font-serif text-2xl font-semibold text-slate-900 mb-2">
              Something Went Wrong
            </h2>
            <p className="text-slate-600 mb-8">
              {status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your donation. Please contact us if you believe this is an error."}
            </p>
            <Link to="/donate">
              <Button className="bg-amber-700 hover:bg-amber-800 text-white">
                Try Again
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-50" data-testid="donate-success-page">
      <div className="max-w-4xl mx-auto px-6 py-24">
        <Card className="shadow-xl border-0 overflow-hidden">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-12 text-center">
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-14 h-14 text-teal-600" />
            </div>
            <h1 className="font-serif text-4xl font-bold text-white mb-4">
              Thank You!
            </h1>
            <p className="text-teal-100 text-lg">
              Your generous donation has been received
            </p>
          </div>
          
          <CardContent className="p-12">
            <div className="text-center mb-8">
              {paymentInfo && (
                <div className="bg-slate-50 rounded-lg p-6 mb-8 inline-block">
                  <p className="text-slate-500 text-sm mb-1">Donation Amount</p>
                  <p className="font-serif text-4xl font-bold text-slate-900">
                    ${(paymentInfo.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}
              
              <p className="text-slate-600 leading-relaxed max-w-lg mx-auto">
                Your support makes a real difference in the lives of sickle cell patients and their families. 
                Together, we're fighting for legislative change, providing patient support, and raising awareness.
              </p>
            </div>

            <div className="border-t border-slate-100 pt-8">
              <h3 className="font-serif text-xl font-semibold text-slate-900 mb-4 text-center">
                What Happens Next?
              </h3>
              <ul className="space-y-3 max-w-md mx-auto">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">A receipt has been sent to your email</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">Your donation is tax-deductible</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-600">You'll receive updates on our impact</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link to="/">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3">
                  Return Home
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/events">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 px-8 py-3">
                  View Upcoming Events
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DonateSuccessPage;
